import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { PageHeader, Spinner, Field, ErrorText, ConfirmDialog, btnPrimary, btnSecondary, btnGhost, btnDanger, inputCls } from "@/components/portal/ui";

const EMPTY = { name: "", description: "", price: "", keywords: "", active: true, order: 0 };

function SpecialtyDialog({ open, onOpenChange, item, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(item ? { ...item, price: item.price ?? "", keywords: (item.keywords || []).join(", ") } : EMPTY);
  }, [open, item]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      name: form.name,
      description: form.description,
      price: form.price === "" ? null : Number(form.price),
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      active: form.active,
      order: Number(form.order) || 0,
    };
    try {
      const res = item ? await api.put(`/specialties/${item.id}`, payload) : await api.post("/specialties", payload);
      toast.success(item ? "Specialità aggiornata" : "Specialità creata");
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-lg" data-testid="specialty-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{item ? "Modifica specialità" : "Nuova specialità"}</DialogTitle>
          <DialogDescription>Le parole chiave servono all'assistente WhatsApp per riconoscere la specialità richiesta.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <Field label="Nome" htmlFor="sp-name"><input id="sp-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="es. Cardiologo" data-testid="specialty-name-input" /></Field>
          <Field label="Descrizione" htmlFor="sp-desc"><textarea id="sp-desc" rows={2} value={form.description} onChange={set("description")} className={inputCls} data-testid="specialty-desc-input" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prezzo indicativo (€)" htmlFor="sp-price"><input id="sp-price" type="number" min={0} step="1" value={form.price} onChange={set("price")} className={inputCls} placeholder="159" data-testid="specialty-price-input" /></Field>
            <Field label="Ordine" htmlFor="sp-order"><input id="sp-order" type="number" value={form.order} onChange={set("order")} className={inputCls} data-testid="specialty-order-input" /></Field>
          </div>
          <Field label="Parole chiave" htmlFor="sp-keywords" hint="Separate da virgola, es. cuore, ecg, pressione">
            <input id="sp-keywords" value={form.keywords} onChange={set("keywords")} className={inputCls} data-testid="specialty-keywords-input" />
          </Field>
          <label className="flex items-center gap-3 text-sm font-semibold">
            <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} className="data-[state=checked]:bg-[#5B2C91]" data-testid="specialty-active-switch" />
            Attiva (visibile e prenotabile)
          </label>
          <ErrorText testid="specialty-form-error">{error}</ErrorText>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className={btnSecondary} data-testid="specialty-cancel-btn">Annulla</button>
            <button type="submit" disabled={saving} className={btnPrimary} data-testid="specialty-save-btn">{saving ? "Salvataggio…" : "Salva"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSpecialties() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/specialties").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const remove = async () => {
    try {
      await api.delete(`/specialties/${toDelete.id}`);
      toast.success("Specialità eliminata");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-specialties">
      <PageHeader
        eyebrow="Specialità"
        title="Specialità mediche"
        desc="Le specialità offerte, con prezzo indicativo e parole chiave per le risposte automatiche su WhatsApp."
        actions={<button onClick={() => setDialog({ open: true, item: null })} className={btnPrimary} data-testid="new-specialty-btn"><Plus className="h-4 w-4" /> Nuova specialità</button>}
      />
      {items === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <div className="overflow-x-auto rounded-3xl bg-white ring-1 ring-[#5B2C91]/8" data-testid="specialties-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Specialità</TableHead>
                <TableHead>Prezzo</TableHead>
                <TableHead>Parole chiave</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`specialty-row-${s.id}`}>
                  <TableCell>
                    <p className="font-semibold">{s.name}</p>
                    {s.description && <p className="max-w-xs text-xs text-[#6B6280]">{s.description}</p>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{s.price != null ? `${s.price} €` : <span className="text-[#8A809A]">—</span>}</TableCell>
                  <TableCell>
                    <div className="flex max-w-sm flex-wrap gap-1">
                      {(s.keywords || []).map((k) => <span key={k} className="rounded-full bg-[#F6F4FA] px-2 py-0.5 text-[11px] text-[#4A4059]">{k}</span>)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`specialty-active-${s.id}`}>{s.active ? "Attiva" : "Disattivata"}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setDialog({ open: true, item: s })} className={btnGhost} aria-label="Modifica" data-testid={`specialty-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setToDelete(s)} className={btnDanger} aria-label="Elimina" data-testid={`specialty-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <SpecialtyDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} item={dialog.item} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Eliminare la specialità?" desc={toDelete ? `"${toDelete.name}" verrà rimossa anche dai profili dei medici.` : ""} onConfirm={remove} confirmLabel="Elimina" destructive />
    </div>
  );
}
